<template>
	<v-container
		fluid
		grid-list-xl
	>
		<v-row>
			<h3>{{ fileName }} PDF</h3>
		</v-row>
		<v-row>
			<v-file-input
				v-model="newFile"
				hide-details
				:label="`Upload ${ fileName } PDF...`"
				accept=".pdf"
				:error="fileError"
				variant="outlined"
				@change="fileError = false"
			/>
		</v-row>
		<v-row
			class="d-flex align-center"
		>
			<v-col>
				<v-select
					v-model="selectedLanguage"
					variant="outlined"
					hide-details
					:items="languages"
					item-title="name"
					item-value="id"
					:error="languageError"
					label="Select language..."
					@update:model-value="languageError = false"
				/>
			</v-col>
			<v-col>
				<v-btn
					size="large"
					color="primary"
					:loading="uploading"
					:disabled="uploading"
					@click="submit()"
				>
					<v-icon icon="$cloudUpload" />
				</v-btn>
			</v-col>
		</v-row>
		<v-row no-gutters>
			<v-col>
				<v-table
					v-if="files.length > 0"
				>
					<thead>
						<tr>
							<th class="text-left">
								Language
							</th>
							<th class="text-left">
								File Name
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="pdf in files"
							:key="pdf.language.name"
						>
							<td>{{ pdf.language.name }}</td>
							<td
								style="text-decoration: underline"
								:style="{cursor: 'pointer'}"
								@click="openPdf(pdf)"
								@click.middle="openPdf(pdf, true)"
							>
								<v-tooltip location="top">
									<template #activator="{ props }">
										<span v-bind="props">{{ truncateFileName(pdf.fileName) }}</span>
									</template>
									<span>{{ pdf.fileName }}</span>
								</v-tooltip>
							</td>
						</tr>
					</tbody>
				</v-table>
				<h5 v-else>
					No {{ fileName }} files available
				</h5>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import {
	axios,
} from '../../services/axios-service.js';
import displayError from 'laravel-front-library/src/utils/displayError/displayError';

export default {
	props: {
		fileName: {
			type: String,
			required: true,
		},

		tableName: {
			type: String,
			required: true,
		},

		fileLocation: {
			type: String,
			required: true,
		},

		id: {
			type: Number,
			required: true,
		},

		identifyBy: {
			type: String,
			required: true
		}
	},

	data () {
		return {
			newFile: null,
			files: [],
			languages: [],
			selectedLanguage: null,
			uploading: false,
			fileError: false,
			languageError: false,
		};
	},

	async mounted () {
		await this.getSupportedLanguages();
		this.retrieveFiles();
	},

	methods: {

		/**
		 * Custom truncation function to shrink the file name to the right length while still keeping the head
		 * and tail of the name
		 *
		 * @param {string} name - Full lenght file name
		 * @returns {string|*} - File name truncated
		 */
		truncateFileName (name) {
			if (name.length > 20) {
				return name.substr(0, 10) + '...' + name.substr(name.length - 10, name.length);
			}
			return name;

		},

		/**
		 * Retrieves a list of languages that we currently support. For the foreseeable future this will be:
		 * { English, Dutch, German }
		 *
		 * @returns {Promise}
		 */
		getSupportedLanguages () {
			return new Promise((resolve, reject) => {
				axios
					.get('/api/v0/languages/supported')
					.then(({
						data: languages
					}) => {
						this.languages = languages;
						if (this.languages) {
							this.selectedLanguage = this.languages[0].id;
						}
						resolve();
					})
					.catch((error) => {
						displayError(this.$gettext('Something has gone wrong during data retrieval'));
						window.captureException(error);
						reject();
					});
			});
		},

		/**
		 * Retrieve the list of pdfs from retrieveLocation to be listed on the table
		 *
		 * @returns {void}
		 */
		retrieveFiles () {
			axios
				.get(`/api/v0/${this.tableName}/latest?${this.identifyBy}=${this.id}`)
				.then(({
					data: files,
				}) => {
					this.files = files.sort((a, b) => {
						return a.language.name > b.language.name ? 1 : -1;
					});
				})
				.catch((error) => {
					window.captureException(error);
				});
		},

		/**
		 * Download file when clicked
		 *
		 * @param {object} pdf - PDF object
		 * @param {boolean} newTab - Indicates whether to open file in new tab or not
		 * @returns {void}
		 */
		openPdf (pdf, newTab = false) {
			const link = `${this.fileLocation}?id=${pdf.id}`;

			if (newTab) {
				window.open(link);
			} else {
				window.location.href = link;
			}
		},

		/**
		 * Submit a new PDF
		 *
		 * @returns {void}
		 */
		submit () {
			this.uploading = true;

			if (!this.newFile) {
				this.fileError = true;
				this.uploading = false;
			}

			if (!this.selectedLanguage) {
				this.languageError = true;
				this.uploading = false;
			}

			if (!this.languageError && !this.fileError) {
				const formData = new FormData();

				formData.append(this.identifyBy, this.id);
				formData.append('file', this.newFile[0]);
				formData.append('language_id', this.selectedLanguage);
				axios
					.post(`/api/v0/${this.tableName}`, formData, {
						headers: {
							'Content-Type': 'multipart/form-data',
						},
					})
					.then(() => {
						this.newFile = null;
						this.retrieveFiles();
					})
					.catch((error) => {
						window.captureException(error);
					})
					.finally(() => {
						this.uploading = false;
					});
			}
		},
	},
};
</script>

